import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Compass, Video, Store, BookOpen, Wand2, ArrowRight, X, PlayCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TourPlayer from "@/components/TourPlayer";

const ZONES = [
  { icon: Compass, title: "Social", desc: "Living feed that evolves with you. Emotion reactions. AI-enhanced posts." },
  { icon: Video, title: "Creator", desc: "AI editing, growth coach, revenue per engagement quality." },
  { icon: Store, title: "Marketplace", desc: "Digital storefronts. AI negotiation. Stripe-powered checkout." },
  { icon: BookOpen, title: "Knowledge", desc: "Verified knowledge graphs. AI tutors. Personalized paths." },
  { icon: Wand2, title: "AI Studio", desc: "Idea to image in seconds. Nano Banana image generation." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, login, signup } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/social", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await signup(form.email, form.password, form.name || form.email.split("@")[0]);
      }
      toast.success("Welcome to NEXUS");
      navigate("/social");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/social";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6 sm:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7B2CBF] flex items-center justify-center glow-ring">
            <Sparkles size={20} className="text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-xl tracking-tight">NEXUS</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]">AI Social Intelligence</div>
          </div>
        </div>
        <button
          data-testid="open-auth-btn"
          onClick={() => setAuthOpen(true)}
          className="nexus-btn-primary text-sm"
        >
          Enter NEXUS
        </button>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6 sm:px-12">
        <div
          className="absolute inset-0 -z-10 opacity-70"
          style={{
            background:
              "radial-gradient(circle at 30% 20%, rgba(255,138,101,0.35), transparent 45%), radial-gradient(circle at 70% 80%, rgba(14,165,233,0.30), transparent 50%), radial-gradient(circle at 80% 30%, rgba(221,42,123,0.28), transparent 50%)",
          }}
        />
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#00E5FF]">a living digital intelligence</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-black tracking-tighter leading-[0.95]">
              Build your<br />
              <span className="gradient-text">digital empire</span><br />
              on one intelligence.
            </h1>
            <p className="mt-6 text-lg text-[#64748B] max-w-xl leading-relaxed">
              NEXUS unifies social, creator, marketplace, knowledge and AI studio into
              a single operating system. AI is not a feature — it is the substrate.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-cta-primary"
                onClick={() => setAuthOpen(true)}
                className="nexus-btn-primary text-base flex items-center gap-2"
              >
                Begin <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-cta-google"
                onClick={googleLogin}
                className="nexus-btn-secondary text-base"
              >
                Continue with Google
              </button>
              <button
                data-testid="hero-cta-tour"
                onClick={() => setTourOpen(true)}
                className="nexus-btn-secondary text-base flex items-center gap-2"
              >
                <PlayCircle size={16} /> Watch 2-min tour
              </button>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
              <span>GPT-5.2</span>
              <span className="text-[#7B2CBF]">·</span>
              <span>Claude Sonnet 4.5</span>
              <span className="text-[#7B2CBF]">·</span>
              <span>Gemini 3 Flash</span>
              <span className="text-[#7B2CBF]">·</span>
              <span>Nano Banana</span>
              <span className="text-[#7B2CBF]">·</span>
              <span>Stripe</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative glass-heavy rounded-3xl p-4 grain">
              <TourPlayer variant="inline" onClose={() => setTourOpen(true)} />
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="glass rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#00E5FF]">Reach</div>
                  <div className="font-display text-2xl">2.4M</div>
                </div>
                <div className="glass rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5C542]">Revenue</div>
                  <div className="font-display text-2xl">$48,210</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Zones */}
      <section className="px-6 sm:px-12 pb-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-2">five intelligent zones</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">One operating system. Five lives.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {ZONES.map((z, i) => (
              <motion.div
                key={z.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl p-6 hover:border-[#00E5FF]/30 transition-all hover:-translate-y-1"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF]/20 to-[#7B2CBF]/20 flex items-center justify-center mb-4">
                  <z.icon size={18} className="text-[#00E5FF]" />
                </div>
                <div className="font-display font-bold text-lg mb-2">{z.title}</div>
                <p className="text-sm text-[#64748B] leading-relaxed">{z.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Auth modal */}
      {authOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(10px)" }}
          onClick={() => setAuthOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-heavy rounded-3xl p-8 max-w-md w-full grain"
            data-testid="auth-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF]">{mode === "login" ? "Re-enter" : "Initialise"}</div>
                <h3 className="font-display text-2xl font-bold mt-1">{mode === "login" ? "Welcome back" : "Create your empire"}</h3>
              </div>
              <button onClick={() => setAuthOpen(false)} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>

            <button
              data-testid="google-login-btn"
              onClick={googleLogin}
              className="w-full nexus-btn-secondary mb-3 flex items-center justify-center gap-3"
            >
              <span className="w-5 h-5 rounded-full bg-[#0F172A] flex items-center justify-center text-xs font-bold text-white">G</span>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-[#94A3B8] uppercase tracking-[0.2em]">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <input
                  data-testid="signup-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="nexus-input"
                />
              )}
              <input
                data-testid="auth-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="nexus-input"
              />
              <input
                data-testid="auth-password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                className="nexus-input"
              />
              <button
                data-testid="auth-submit"
                disabled={busy}
                className="w-full nexus-btn-primary disabled:opacity-50"
              >
                {busy ? "…" : mode === "login" ? "Enter NEXUS" : "Create account"}
              </button>
            </form>

            <div className="mt-4 text-center text-sm text-[#64748B]">
              {mode === "login" ? (
                <>No identity yet?{" "}
                  <button
                    data-testid="switch-to-signup"
                    onClick={() => setMode("signup")}
                    className="text-[#00E5FF] hover:underline"
                  >
                    Create one
                  </button>
                </>
              ) : (
                <>Already aboard?{" "}
                  <button
                    data-testid="switch-to-login"
                    onClick={() => setMode("login")}
                    className="text-[#00E5FF] hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {tourOpen && (
        <TourPlayer
          variant="modal"
          onClose={() => setTourOpen(false)}
          onSignup={() => { setTourOpen(false); setMode("signup"); setAuthOpen(true); }}
        />
      )}
    </div>
  );
}
